import { Link, useParams } from "react-router-dom";
import { getNewsById } from "@/lib/data";
import { news } from "@/mocks/news";
import type { NewsItem } from "@/types";
import Breadcrumb from "@/components/base/Breadcrumb";
import EmptyState from "@/components/base/EmptyState";
import { usePageMeta } from "@/hooks/usePageMeta";

const all = news as NewsItem[];

export default function NewsDetail() {
  const { id } = useParams<{ id: string }>();
  const item = id ? getNewsById(id) : undefined;

  usePageMeta(
    item ? `${item.title} - 漂亮的保姆` : "影视资讯 - 漂亮的保姆",
    item ? item.summary : "在漂亮的保姆查看影视剧资讯详情。",
    item ? `${item.category},${item.title},漂亮的保姆,影视资讯` : "影视资讯,漂亮的保姆",
  );

  if (!item) {
    return (
      <div className="max-w-[1200px] mx-auto px-4 md:px-6 py-16">
        <EmptyState
          title="未找到该资讯"
          desc="你访问的资讯可能已下线，可以返回资讯列表浏览其他内容。"
          actionText="返回影视资讯"
          onAction={() => window.REACT_APP_NAVIGATE("/news")}
        />
      </div>
    );
  }

  const related = all.filter((n) => n.id !== item.id).slice(0, 4);

  return (
    <>
      <div className="bg-background-50 border-b border-background-200">
        <div className="max-w-[1200px] mx-auto px-4 md:px-6 py-5">
          <Breadcrumb
            items={[
              { label: "首页", to: "/" },
              { label: "影视资讯", to: "/news" },
              { label: item.category },
            ]}
          />
        </div>
      </div>

      <div className="max-w-[1200px] mx-auto px-4 md:px-6 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <article className="lg:col-span-2 min-w-0">
            <span className="px-2.5 py-1 rounded-md bg-accent-100 text-accent-700 text-xs font-medium">
              {item.category}
            </span>
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mt-4 leading-snug">
              {item.title}
            </h1>
            <p className="mt-3 text-xs md:text-sm text-foreground-400">
              发布于 {item.date} · 漂亮的保姆
            </p>
            <div className="w-full aspect-[16/9] rounded-lg overflow-hidden bg-background-200 my-6">
              <img
                src={item.cover}
                alt={item.title}
                title={`${item.title} - 漂亮的保姆`}
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div className="space-y-4">
              {item.content.map((p, i) => (
                <p
                  key={i}
                  className="text-sm md:text-base text-foreground-700 leading-loose"
                >
                  {p}
                </p>
              ))}
            </div>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/news"
                className="inline-flex items-center gap-2 h-10 px-5 rounded-md bg-background-50 border border-background-300 text-foreground-700 text-sm font-medium hover:border-primary-400 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-arrow-left-line" />
                返回资讯列表
              </Link>
            </div>
          </article>

          <aside className="lg:col-span-1">
            <div className="bg-background-50 rounded-lg border border-background-200 p-4 md:p-5 lg:sticky lg:top-24">
              <div className="flex items-center gap-3 mb-4">
                <span className="w-1.5 h-6 rounded-full bg-primary-500" />
                <h2 className="font-heading text-lg font-bold text-foreground-950">
                  相关阅读
                </h2>
              </div>
              <ul className="space-y-3">
                {related.map((n) => (
                  <li key={n.id}>
                    <Link
                      to={`/news/${n.id}`}
                      className="group flex gap-3 cursor-pointer"
                    >
                      <div className="w-20 h-14 rounded-md overflow-hidden bg-background-200 shrink-0">
                        <img
                          src={n.cover}
                          alt={n.title}
                          title={`${n.title} - 漂亮的保姆`}
                          className="w-full h-full object-cover object-top"
                        />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm text-foreground-800 group-hover:text-primary-600 transition-colors line-clamp-2">
                          {n.title}
                        </p>
                        <p className="text-xs text-foreground-400 mt-1">{n.date}</p>
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </aside>
        </div>
      </div>
    </>
  );
}